import { buildApiFetchRequest, ensureWarmup, recoverApiSession, redirectToLogin } from "./client";

type StreamEvent = {
    event: string;
    data: string;
};

type OpenApiEventStreamOptions = {
    onEvent: (event: StreamEvent) => void;
    onError?: (error: unknown) => void;
    reconnectDelayMs?: number;
};

function parseEventBlock(block: string): StreamEvent | null {
    const lines = block.split("\n");
    let event = "message";
    const dataLines: string[] = [];

    for (const line of lines) {
        if (!line || line.startsWith(":")) {
            continue;
        }

        if (line.startsWith("event:")) {
            event = line.slice(6).trim() || "message";
            continue;
        }

        if (line.startsWith("data:")) {
            dataLines.push(line.slice(5).trimStart());
        }
    }

    if (dataLines.length === 0) {
        return null;
    }

    return {
        event,
        data: dataLines.join("\n"),
    };
}

async function consumeEventStream(
    response: Response,
    signal: AbortSignal,
    onEvent: (event: StreamEvent) => void
): Promise<void> {
    if (!response.body) {
        throw new Error("Stream body is missing");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder("utf-8");
    let buffer = "";

    while (!signal.aborted) {
        const { value, done } = await reader.read();
        if (done) {
            buffer += decoder.decode();
            break;
        }

        buffer += decoder.decode(value, { stream: true });
        buffer = buffer.replace(/\r\n/g, "\n");

        let separatorIndex = buffer.indexOf("\n\n");
        while (separatorIndex >= 0) {
            const block = buffer.slice(0, separatorIndex);
            buffer = buffer.slice(separatorIndex + 2);

            const parsed = parseEventBlock(block);
            if (parsed) {
                onEvent(parsed);
            }

            separatorIndex = buffer.indexOf("\n\n");
        }
    }

    const tail = buffer.trim();
    if (tail) {
        const parsed = parseEventBlock(tail);
        if (parsed) {
            onEvent(parsed);
        }
    }
}

export function openApiEventStream(path: string | (() => string), options: OpenApiEventStreamOptions): () => void {
    const reconnectDelayMs = options.reconnectDelayMs ?? 2000;
    let abortController: AbortController | null = null;
    let closed = false;
    let reconnectTimer: number | null = null;

    const clearReconnectTimer = () => {
        if (reconnectTimer !== null) {
            window.clearTimeout(reconnectTimer);
            reconnectTimer = null;
        }
    };

    const scheduleReconnect = () => {
        clearReconnectTimer();
        if (closed || document.hidden) {
            return;
        }

        reconnectTimer = window.setTimeout(() => {
            void connect();
        }, reconnectDelayMs);
    };

    const connect = async (): Promise<void> => {
        if (closed || document.hidden || abortController) return;
        const controller = new AbortController();
        abortController = controller;
        const stopped = () => closed || controller.signal.aborted;
        try {
            await ensureWarmup();
            if (stopped()) return;
            const nextPath = typeof path === "function" ? path() : path;
            const fetchStream = async () => {
                const request = await buildApiFetchRequest(nextPath, {
                    method: "GET",
                    headers: {
                        Accept: "text/event-stream",
                        "Cache-Control": "no-cache",
                    },
                    signal: controller.signal,
                    cache: "no-store",
                });
                if (stopped()) throw new Error("Stream closed");
                return fetch(request.url, request.init);
            };

            let response = await fetchStream();
            if (stopped()) return;
            if (response.status === 401) {
                await response.body?.cancel();
                const recovery = await recoverApiSession();
                if (stopped()) return;
                if (recovery === "unauthorized") {
                    closed = true;
                    redirectToLogin();
                }
                if (recovery !== "refreshed") throw new Error("Session temporarily unavailable");
                response = await fetchStream();
                if (stopped()) return;
                if (response.status === 401) {
                    await response.body?.cancel();
                    closed = true;
                    redirectToLogin();
                }
            }

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            await consumeEventStream(response, controller.signal, options.onEvent);
        } catch (error) {
            if (!stopped()) options.onError?.(error);
        } finally {
            if (abortController === controller) abortController = null;
            if (!closed && !document.hidden) scheduleReconnect();
        }
    };

    const handleVisibility = () => {
        clearReconnectTimer();
        if (document.hidden) {
            abortController?.abort();
            abortController = null;
        } else {
            void connect();
        }
    };

    document.addEventListener("visibilitychange", handleVisibility);
    void connect();

    return () => {
        closed = true;
        document.removeEventListener("visibilitychange", handleVisibility);
        clearReconnectTimer();
        abortController?.abort();
    };
}
